/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
module.exports = {
	functions: {
		CHOOSE: {
			default: {
				category: 'Lookup',
				description: 'Returns the value at the specified index from a list of passed arguments.',
				inlineDescription: 'Returns the value at specified index from parameter list',
				arguments: [
					{
						type: 'Number',
						name: 'Index',
						description: 'Index of item to retrieve',
						optional: false
					},
					{
						type: 'Value',
						name: 'Value1',
						description: 'First value in list',
						optional: false
					},
					{
						type: 'Value',
						name: 'Value2, ...',
						description: 'Other values for the list',
						optional: true
					}
				],
				return: {
					type: 'Value',
					description: 'The value at specified index or #VALUE!.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=CHOOSE(2, "German", "English")',
							result: 'English',
							comment: 'Index 2 represents "English"'
						}
					]
				}
			}
		},
		COLUMN: {
			default: {
				category: 'Lookup',
				description: 'Returns the column number of given cell reference or cell range. For ranges the top-left cell is used as pivot cell.',
				inlineDescription: 'Returns the column number of given cell reference or cell range',
				arguments: [
					{
						type: 'Range',
						name: 'Cell or Range',
						description: 'Cell or cell range to get the column number for. If not given current cell is used.',
						optional: true
					}
				],
				return: {
					type: 'Number',
					description: 'The column number.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=COLUMN(B6)',
							result: 2,
							comment: 'Column B corresponds to number 2'
						}
					]
				}
			}
		},
		COLUMNS: {
			default: {
				category: 'Lookup',
				description: 'Returns the number of columns for given cell range.',
				inlineDescription: 'Returns the number of columns for given cell range',
				arguments: [
					{
						type: 'Range',
						name: 'Range',
						description: 'Cell range to get the number of columns for',
						optional: false
					}
				],
				return: {
					type: 'Number',
					description: 'The number of columns.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=COLUMNS(A1:C4)',
							result: 3,
							comment: 'Number of columns for A1:C4 is 3'
						}
					]
				}
			}
		},
		DAVERAGE: {
			default: {
				category: 'Lookup',
				description: 'Returns the average of a specified column in given data range.',
				inlineDescription: 'Returns the average of all cells at specified column in rows which match given criteria',
				arguments: [
					{
						type: 'Range',
						name: 'DataRange',
						description: 'Cell range, that contains the data to evaluate. The range must include the field labels.',
						optional: false
					},
					{
						type: 'Number',
						name: 'ColumnIndex',
						description: 'Column label or index to calculate average for.',
						optional: false
					},
					{
						type: 'Range',
						name: 'CriteriaRange',
						description: 'Specifies the conditions a row in cell range must fulfill. Must define at least one column with a label and a cell below which specifies the condition.',
						optional: false
					}
				],
				return: {
					type: 'Number',
					description: 'The average of the matching column.'
				},
				examples: {
					infoStart: '\n| | A | B | C |\n' +
						'|---|---|---|---|\n' +
						'|1|Name|Age|City|\n' +
						'|2| | |Berlin|\n' +
						'|3| | | |\n' +
						'|4|Name|Age|City|\n' +
						'|5|Miller|24|Berlin|\n' +
						'|6|Mayer|30|Oslo|\n' +
						'|7|Smith|26|Munich|\n' +
						'|8|Jones|19|Berlin|\n',
					infoEnd: '',
					formulas: [
						{
							formula: '=DAVERAGE(A4:C8,2,A1:C2)',
							result: '21.5',
							comment: 'Evaluates the average age in "Berlin"'
						}
					]
				}
			}
		},
		DCOUNT: {
			default: {
				category: 'Lookup',
				description: 'Counts all cells that contain a number within a cell range.',
				inlineDescription: 'Counts all cells that represent a number at specified column in rows which match given criterias ',
				arguments: [
					{
						type: 'Range',
						name: 'DataRange',
						description: 'Cell range, that contains the data to evaluate. The range must include the field labels.',
						optional: false
					},
					{
						type: 'Number',
						name: 'ColumnIndex',
						description: 'Column label or index to count values in.',
						optional: false
					},
					{
						type: 'Range',
						name: 'CriteriaRange',
						description: 'Specifies the conditions a row in cell range must fulfill. Must define at least one column with a label and a cell below which specifies the condition.',
						optional: false
					}
				],
				return: {
					type: 'Number',
					description: 'The counted amount of cells within the given parameters.'
				},
				examples: {
					infoStart: '\n| | A | B | C |\n' +
						'|---|---|---|---|\n' +
						'|1|Name|Age|City|\n' +
						'|2| | |Berlin|\n' +
						'|3| | | |\n' +
						'|4|Name|Age|City|\n' +
						'|5|Miller|24|Berlin|\n' +
						'|6|Mayer|30|Oslo|\n' +
						'|7|Smith|26|Munich|\n' +
						'|8|Jones|19|Berlin|\n',
					infoEnd: '',
					formulas: [
						{
							formula: '=DCOUNT(A4:C8,2,A1:C2)',
							result: '2',
							comment: 'Evaluates the number of valid age entries in "Berlin".'
						}
					]
				}
			}
		},
		DMAX: {
			default: {
				category: 'Lookup',
				description: 'Returns the largest number in a column of cell range which matches the specified criteria.',
				inlineDescription: 'Returns the largest number of all cells at specified column in rows which match given criterias ',
				arguments: [
					{
						type: 'Range',
						name: 'DataRange',
						description: 'Cell range, that contains the data to evaluate. The range must include the field labels.',
						optional: false
					},
					{
						type: 'Number',
						name: 'ColumnIndex',
						description: 'Column label or index to find the maximum value in.',
						optional: false
					},
					{
						type: 'Range',
						name: 'CriteriaRange',
						description: 'Specifies the conditions a row in cell range must fulfill. Must define at least one column with a label and a cell below which specifies the condition.',
						optional: false
					}
				],
				return: {
					type: 'Number',
					description: 'The maximum value of the matching column.'
				},
				examples: {
					infoStart: '\n| | A | B | C |\n' +
						'|---|---|---|---|\n' +
						'|1|Name|Age|City|\n' +
						'|2| | |Berlin|\n' +
						'|3| | | |\n' +
						'|4|Name|Age|City|\n' +
						'|5|Miller|24|Berlin|\n' +
						'|6|Mayer|30|Oslo|\n' +
						'|7|Smith|26|Munich|\n' +
						'|8|Jones|19|Berlin|\n',
					infoEnd: '',
					formulas: [
						{
							formula: '=DMAX(A4:C8,2,A1:C2)',
							result: '24',
							comment: 'Evaluates the maximum of valid age entries in "Berlin".'
						}
					]
				}
			}
		},
		DMIN: {
			default: {
				category: 'Lookup',
				description: 'Returns the smallest number in a column of cell range which matches the specified criteria.',
				inlineDescription: 'Returns the smallest number of all cells at specified column in rows which match given criteria',
				arguments: [
					{
						type: 'Range',
						name: 'DataRange',
						description: 'Cell range, that contains the data to evaluate. The range must include the field labels.',
						optional: false
					},
					{
						type: 'Number',
						name: 'ColumnIndex',
						description: 'Column label or index to find the minimum value in.',
						optional: false
					},
					{
						type: 'Range',
						name: 'CriteriaRange',
						description: 'Specifies the conditions a row in cell range must fulfill. Must define at least one column with a label and a cell below which specifies the condition.',
						optional: false
					}
				],
				return: {
					type: 'Number',
					description: 'The minimum value of the matching column.'
				},
				examples: {
					infoStart: '\n| | A | B | C |\n' +
						'|---|---|---|---|\n' +
						'|1|Name|Age|City|\n' +
						'|2| | |Berlin|\n' +
						'|3| | | |\n' +
						'|4|Name|Age|City|\n' +
						'|5|Miller|24|Berlin|\n' +
						'|6|Mayer|30|Oslo|\n' +
						'|7|Smith|26|Munich|\n' +
						'|8|Jones|19|Berlin|\n',
					infoEnd: '',
					formulas: [
						{
							formula: '=DMIN(A4:C8,2,A1:C2)',
							result: '19',
							comment: 'Evaluates the minimum of valid age entries in "Berlin".'
						}
					]
				}
			}
		},
		DSUM: {
			default: {
				category: 'Lookup',
				description: 'Returns the sum of all values in a column of the cell range which matches the specified criteria.',
				inlineDescription: 'Adds all numbers at specified column in rows which match given criterias ',
				arguments: [
					{
						type: 'Range',
						name: 'DataRange',
						description: 'Cell range, that contains the data to evaluate. The range must include the field labels.',
						optional: false
					},
					{
						type: 'Number',
						name: 'ColumnIndex',
						description: 'Column label or index to calculate the sum for.',
						optional: false
					},
					{
						type: 'Range',
						name: 'CriteriaRange',
						description: 'Specifies the conditions a row in cell range must fulfill. Must define at least one column with a label and a cell below which specifies the condition.',
						optional: false
					}
				],
				return: {
					type: 'Number',
					description: 'The sum of all values of the matching column.'
				},
				examples: {
					infoStart: '\n| | A | B | C |\n' +
						'|---|---|---|---|\n' +
						'|1|Name|Age|City|\n' +
						'|2| | |Berlin|\n' +
						'|3| | | |\n' +
						'|4|Name|Age|City|\n' +
						'|5|Miller|24|Berlin|\n' +
						'|6|Mayer|30|Oslo|\n' +
						'|7|Smith|26|Munich|\n' +
						'|8|Jones|19|Berlin|\n',
					infoEnd: '',
					formulas: [
						{
							formula: '=DSUM(A4:C8,2,A1:C2)',
							result: '43',
							comment: 'Evaluates the sum of all ages in "Berlin".'
						}
					]
				}
			}
		},
		INDEX: {
			default: {
				category: 'Lookup',
				description: 'Returns a cell within the range using the row and column offset. If row or column is 0, INDEX returns the complete column or row respectively.',
				inlineDescription: 'Returns a cell within the range using the row and column offset',
				arguments: [
					{
						type: 'Range',
						name: 'Range',
						description: 'Range of cells to search in.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Row',
						description: 'Row index or offset starting with 1. Specify 0 to return complete column.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Column',
						description: 'Column index offset starting with 1. Specify 1 to return complete row.',
						optional: false
					}
				],
				return: {
					type: 'Value',
					description: 'Cell value of the cell within the range with an offset of row and column.'
				},
				examples: {
					infoStart: '\n| | A | B |\n' +
						'|---|---|---|\n' +
						'|1|2|3|\n' +
						'|2|8|4|\n' +
						'|3|1|3|\n',
					infoEnd: '',
					formulas: [
						{
							formula: '=INDEX(A1:B3,2,1)',
							result: '8',
							comment: 'The value of cell A2 is returned.'
						}
					]
				}
			}
		},
		INDIRECT: {
			default: {
				category: 'Lookup',
				description: 'Returns the value of the reference specified by a text string.',
				inlineDescription: 'Returns the value of the reference specified by passed text string',
				arguments: [
					{
						type: 'String',
						name: 'RefText',
						description: 'A textual representation of a cell reference, cell range reference or a named cell.',
						optional: false
					},
					{
						type: 'Boolean',
						name: 'R1C1Style',
						description: 'If set to FALSE the given RefText is interpreted as R1C1-style reference. ',
						default: true,
						optional: true
					}
				],
				return: {
					type: 'Number',
					description: 'The value at specified reference or #REF! if reference could not be created. Referencing to an indirect() function will result in handing over the reference build with the indirect cell not handing over the value represented by indirect().'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=INDIRECT("A1")',
							result: '10',
							comment: 'Returns the value of cell A1'
						},
						{
							formula: '=INDIRECT("A" & A5)',
							result: '42',
							comment: 'Assuming that A5 contains the value 4 the result represents the value of cell A4'
						}
					]
				}
			}
		},
		MATCH: {
			default: {
				category: 'Lookup',
				description: 'Returns a relative position number of a matching cell inside a specified cell range.',
				inlineDescription: 'Returns relative column index of matching cell inside specified cell range',
				arguments: [
					{
						type: 'Number',
						name: 'value',
						description: 'Value to search for.',
						optional: false
					},
					{
						type: 'Range',
						name: 'SearchRange',
						description: 'A cell range to search in.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Type',
						description: 'Search type with the following options: <br /> `1`: (Default) Find the largest value in cell range which is less than or equal to specified value. Table must be in ascending order. <br />`0`: Find the first value which match exactly specified value. Here the wildcards ? and * are supported. <br /> `-1`: Find the smallest value in cell range which is greater or equal to specified value. Table must be in descending order.',
						optional: true
					}
				],
				return: {
					type: 'Number',
					description: 'Returns relative position of in cell-range for given value or #NA if no match is found.'
				},
				examples: {
					infoStart: '\n| |A|B|\n|' +
						'|---|---|---|\n' +
						'|**1**|2|3|\n' +
						'|**2**|8|4|\n' +
						'|**3**|1|3|\n',
					infoEnd: '',
					formulas: [
						{
							formula: '=MATCH(4,B1:B3, 0)',
							result: '2',
							comment: 'The index of the cell with the searched value is returned.'
						},
						{
							formula: '=MATCH(2,A1:B1, 0)',
							result: '1',
							comment: 'The index of the cell with the searched value is returned.'
						}
					]
				}
			}
		},
		OFFSET: {
			default: {
				category: 'Lookup',
				description: 'Returns a cell range that is defined by specified number of rows and columns from an initial range.',
				inlineDescription: 'Returns range of cells that is a specified number of rows and columns from an initial specified range.',
				arguments: [
					{
						type: 'Range',
						name: 'Range',
						description: 'A cell or cell range which defines the base of returned cell range.',
						optional: false
					},
					{
						type: 'Number',
						name: 'RowOffset',
						description: 'Row offset from top left position of Range, might be positive or negative.',
						optional: false
					},
					{
						type: 'Number',
						name: 'ColumnOffset',
						description: 'Column offset from top left position of Range, might be positive or negative.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Height',
						description: 'Number of rows for result range. Height must be greater than zero. If not given the height of specified base cell range is used.',
						optional: true
					},
					{
						type: 'Number',
						name: 'Width',
						description: 'Number of columns for result range. Width must be greater than zero. If not given the width of specified base cell range is used.',
						optional: true
					}
				],
				return: {
					type: 'Number',
					description: 'If height and width define a cell: Value of cell   If height and width define a cell range: #VALUE'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=OFFSET(A2, 2, 1, 1, 1)',
							result: '6',
							comment: 'B4 has an offset of 2 rows and 1 column from A1. The cell value is 6.'
						},
						{
							formula: '=SUM(OFFSET(A2, 1, 1, 2, 1))',
							result: '10',
							comment: 'B3 has an offset of 1 row and 1 column from A1. The SUM of the range defined through height and width is 10.'
						}
					]
				}
			}
		},
		ROW: {
			default: {
				category: 'Lookup',
				description: 'Returns the row number of given cell reference or cell range. For ranges the top-left cell is used as pivot cell.',
				inlineDescription: 'Returns the row number of given cell reference or cell range',
				arguments: [
					{
						type: 'Range',
						name: 'Cell or Range',
						description: 'Cell or cell range to get the row number for. If not given current cell is used',
						optional: true
					}
				],
				return: {
					type: 'Number',
					description: 'The row number.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=ROW(A1:B4)',
							result: 1,
							comment: 'The row number of given range 1'
						}
					]
				}
			}
		},
		ROWS: {
			default: {
				category: 'Lookup',
				description: 'Returns the number of rows for given cell range.',
				inlineDescription: 'Returns the number of rows for given cell range',
				arguments: [
					{
						type: 'Range',
						name: 'Range',
						description: 'Cell range to get the number of rows for',
						optional: false
					}
				],
				return: {
					type: 'Number',
					description: 'The number of rows.'
				},
				examples: {
					infoStart: '',
					infoEnd: '',
					formulas: [
						{
							formula: '=ROWS(A1:C4)',
							result: 4,
							comment: 'Number of rows for A1:C4 is 4'
						}
					]
				}
			}
		},
		VLOOKUP: {
			default: {
				category: 'Lookup',
				description: 'Returns the value of a cell within a specified cell range in a chosen row and characteristic.',
				inlineDescription: 'Looks in the first column of an array and moves across the row to return the value of a cell',
				arguments: [
					{
						type: 'Number',
						name: 'Value',
						description: 'The value to look for in the first column of a table.',
						optional: false
					},
					{
						type: 'Range',
						name: 'Table',
						description: 'Range to look in. The table from which to retrieve a value.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Col_Index',
						description: 'The column in the table from which to retrieve a value.',
						optional: false
					},
					{
						type: 'Number',
						name: 'Range_Lookup',
						description: 'Optional TRUE = approximate match (default). FALSE = exact match.',
						optional: false
					}
				],
				return: {
					type: 'Number',
					description: 'Value of cell within the given range and index or offset, if a row with the value was found.'
				},
				examples: {
					infoStart: '\n| | A | B |\n' +
						'|---|---|---|\n' +
						'|1|Test1  |3|\n' +
						'|2|Test2  |4|\n' +
						'|3|Test3  |3|\n',
					infoEnd: '',
					formulas: [
						{
							formula: '=VLOOKUP("Test3",A1:B3, 2)',
							result: '3',
							comment: 'The value of cell B3 within the given range is returned.'
						}
					]
				}
			}
		}
	},
};
